import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { useAttachmentUpload } from '../model/use-attachment-upload';
import { AttachmentTypeSelect } from './attachment-type-select';
import { FileUploadSection } from './file-upload-section';
import { CommentInput } from './comment-input';

interface AttachmentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  consignmentId: string;
  projectId?: number;
  siteId?: number;
  onSuccess?: () => void;
}

export function AttachmentUploadModal({
  isOpen,
  onClose,
  consignmentId,
  projectId,
  siteId,
  onSuccess
}: AttachmentUploadModalProps) {
  const { t } = useTranslation();
  const {
    formData,
    isLoading,
    handleFileSelect,
    handleRemoveFile,
    handleAttachmentTypeChange,
    handleCommentChange,
    handleSubmit,
    resetForm
  } = useAttachmentUpload({
    consignmentId,
    projectId,
    siteId,
    onSuccess
  });

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isSubmitDisabled = !formData.file || !formData.attachmentType || isLoading;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('newcmr.add.attachment.label')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <AttachmentTypeSelect
            value={formData.attachmentType}
            onChange={handleAttachmentTypeChange}
            disabled={isLoading}
          />

          <FileUploadSection
            file={formData.file}
            onFileSelect={handleFileSelect}
            onFileRemove={handleRemoveFile}
            disabled={isLoading}
          />

          <CommentInput value={formData.comment || ''} onChange={handleCommentChange} disabled={isLoading} />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('gen.cancel.label')}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitDisabled}>
            {isLoading ? t('gen.uploading.label') : t('gen.upload.label')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
